import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BusinessBreakdownDialog from "./bussinessBreakdown.js";
import ConnectorPaymentsDialog from "./connectorPaymentsDialog.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";

const BusinessSummaryBar = ({
  data,
  startDate,
  endDate,
  isIncomeByMis,
  setAlert,
  selectedDsaId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [controller] = useMaterialUIController();
  const { user } = controller;

  //  Local state management
  const [openB, setOpenB] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'received' | 'notReceived'
  const [openC, setOpenC] = useState(false);

  //  Format INR properly
  const formatINR = (amount) =>
    amount != null
      ? amount.toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
        })
      : "₹0.00";

  const giveRequiredUrl = () => {
    if (selectedDsaId != 0) {
      return {
        receivedUrl: URLs.dmaReceviedBusinessBreakdownUrl,
        notReceivedUrl: URLs.dmaNotReceviedBusinessBreakdownUrl,
        allBusinessUrl: URLs.dmaAllBusinessByMisDate,
      };
    } else {
      return {
        receivedUrl: URLs.receivedBusinessBreakdownUrl,
        notReceivedUrl: URLs.notReceivedBusinessBreakdownUrl,
        allBusinessUrl: URLs.khAllDisbursedBusinessUrl,
      };
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        width: "100%",
        gap: 2,
        backgroundColor: "#333333",
        p: 2,
        borderRadius: 2,
        alignItems: "center",
        justifyContent: isMobile ? "center" : "center",
      }}
    >
      {/* Received Business Breakdown */}
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#333333",
          color: "#fff",
          border: "1px solid #555",
          textTransform: "none",
          "&:hover": { backgroundColor: "#555555" },
          fontSize: isMobile ? "0.7rem" : "0.8rem",
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          setDialogType("received");
          setOpenB(true);
        }}
      >
        RECEIVED REVENUE&nbsp;
        <Typography
          component="span"
          sx={{ fontWeight: "bold", fontSize: isMobile ? "0.7rem" : "1rem", color: "#D4AF37" }}
        >
          {formatINR(data?.totalReceivedRevenue?.amount)}
        </Typography>
      </Button>

      <BusinessBreakdownDialog
        open={openB && dialogType === "received"}
        onClose={() => setOpenB(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        isIncomeByMis={isIncomeByMis}
        urlApi={giveRequiredUrl().receivedUrl}
        selectedDsaId={selectedDsaId}
      />

      {/* Not Received Business Breakdown */}
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#333333",
          color: "#fff",
          border: "1px solid #555",
          textTransform: "none",
          "&:hover": { backgroundColor: "#555555" },
          fontSize: isMobile ? "0.7rem" : "0.8rem",
          whiteSpace: "nowrap",
        }}
        onClick={() => {
          setDialogType("notReceived");
          setOpenB(true);
        }}
      >
        NOT RECEIVED REVENUE&nbsp;
        <Typography
          component="span"
          sx={{ fontWeight: "bold", fontSize: isMobile ? "0.7rem" : "1rem", color: "#ff6666" }}
        >
          {formatINR(data?.notReceivedAmount?.amount)}
        </Typography>
      </Button>

      <BusinessBreakdownDialog
        open={openB && dialogType === "notReceived"}
        onClose={() => setOpenB(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        isIncomeByMis={isIncomeByMis}
        urlApi={giveRequiredUrl().notReceivedUrl}
        selectedDsaId={selectedDsaId}
      />

      {/* Connector Payouts */}
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#333333",
          color: "#fff",
          border: "1px solid #555",
          textTransform: "none",
          "&:hover": { backgroundColor: "#555555" },
          fontSize: isMobile ? "0.7rem" : "0.8rem",
        }}
        onClick={() => setOpenC(true)}
      >
        CONNECTOR PAYOUTS
      </Button>

      <ConnectorPaymentsDialog
        open={openC}
        onClose={() => setOpenC(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
      />

      <Box
        sx={{
          ml: isMobile ? 0 : "auto",
          display: "flex",
          justifyContent: isMobile ? "center" : "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#1a1a1a",
            color: "#fff",
            border: "1px solid #555",
            textTransform: "none",
            "&:hover": { backgroundColor: "#2a2a2a" },
            fontSize: isMobile ? "0.8rem" : "0.9rem",
            fontWeight: "bold",
            borderRadius: 2,
            width: isMobile ? "100%" : "auto",
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          onClick={() => {
            setDialogType("All Business");
            setOpenB(true);
          }}
        >
          Total Business:&nbsp;
          <Typography
            component="span"
            sx={{ color: "#D4AF37", fontSize: isMobile ? "0.8rem" : "0.9rem", fontWeight: "bold" }}
          >
            {formatINR(data?.totalBusiness?.amount)}
          </Typography>
        </Button>
      </Box>
      <BusinessBreakdownDialog
        open={openB && dialogType === "All Business"}
        onClose={() => setOpenB(false)}
        startDate={startDate}
        endDate={endDate}
        dsaId={user.companyId || 0}
        user={user}
        setAlert={setAlert}
        isIncomeByMis={isIncomeByMis}
        urlApi={giveRequiredUrl().allBusinessUrl}
        selectedDsaId={selectedDsaId}
      />
    </Box>
  );
};

//  Prop validation
BusinessSummaryBar.propTypes = {
  data: PropTypes.shape({
    totalReceivedRevenue: PropTypes.shape({
      amount: PropTypes.number,
    }),
    notReceivedAmount: PropTypes.shape({
      amount: PropTypes.number,
    }),
    totalBusiness: PropTypes.shape({
      amount: PropTypes.number,
    }),
  }).isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  isIncomeByMis: PropTypes.bool,
  setAlert: PropTypes.func.isRequired,
  selectedDsaId: PropTypes.number,
};

export default BusinessSummaryBar;
